import DebouncedInput from "@/components/DebouncedInput";
import SelectFilter from "@/components/SelectFilter";
import GenericTable from "@/components/GenericTable";
import {
  Alert,
  Box,
  Button,
  Card,
  Heading,
  Input,
  Slider,
  Text,
} from "@chakra-ui/react";
import { useMemo, useState } from "react";
import Kpi from "../components/Kpi";
import StepFooter from "../components/StepFooter";
import { formatCop, formatRate, rateFor, vatLabel } from "../format";
import type { BenchInput } from "../types";
import { useInductionPreApproved } from "../useInductionPreApproved";
import { quoteRowFor } from "../quoteRow";
import { useInductionQuote } from "../useInductionQuote";
import { PCT_STEP } from "../useInductionWizard";
import SelectionRow from "./SelectionRow";

const COLUMNS = [
  { key: "document", label: "Cédula", minWidth: 150 },
  { key: "tenant", label: "Inquilino", minWidth: 180 },
  { key: "address", label: "Inmueble", minWidth: 220 },
  { key: "rent", label: "Canon", minWidth: 130 },
  { key: "endDate", label: "Fin de contrato", minWidth: 140 },
  { key: "billingDay", label: "Cobro del canon", minWidth: 140 },
  { key: "insurer", label: "Aseguradora / Afianzadora", minWidth: 160 },
  { key: "use", label: "Uso", minWidth: 110 },
  { key: "cosigners", label: "Codeudores", minWidth: 120 },
] as const;

type Props = {
  bench: BenchInput;
  withVat: boolean;
  selected: ReadonlySet<string>;
  pct: number;
  search: string;
  provider: string | undefined;
  onPct: (pct: number, orderedIds: string[]) => void;
  onSearch: (search: string) => void;
  onProvider: (provider: string | undefined) => void;
  onBack: () => void;
  onContinue: () => void;
};

const SelectionStep = ({
  bench,
  withVat,
  selected,
  pct,
  search,
  provider,
  onPct,
  onSearch,
  onProvider,
  onBack,
  onContinue,
}: Props): JSX.Element => {
  const { screenings, isLoading, isError, refetch } = useInductionPreApproved();

  // The server's order is the score order; the slider relies on it.
  const orderedIds = useMemo(
    () => screenings?.map((screening) => screening.screeningId) ?? [],
    [screenings]
  );
  const total = orderedIds.length;
  const selectedIds = useMemo(() => [...selected], [selected]);
  const hasSelection = selectedIds.length > 0;

  const {
    quote,
    isFetching,
    isError: isQuoteError,
    refetch: refetchQuote,
    isCurrent,
  } = useInductionQuote({ bench });
  const row = useMemo(
    () => quoteRowFor(quote, orderedIds, selected),
    [quote, orderedIds, selected]
  );

  const providers = useMemo(
    () => [
      ...new Set(
        screenings?.flatMap((screening) =>
          screening.rentGuaranteeProvider
            ? [screening.rentGuaranteeProvider]
            : []
        )
      ),
    ],
    [screenings]
  );

  const visible = useMemo(() => {
    const term = search.trim();
    return screenings?.filter(
      (screening) =>
        (!term || screening.tenantDocumentNumber.includes(term)) &&
        (!provider || screening.rentGuaranteeProvider === provider)
    );
  }, [screenings, search, provider]);

  // What the merchant typed in the count field; null while the slider drives.
  const [draft, setDraft] = useState<string | null>(null);
  const typed = draft === null || draft === "" ? undefined : Number(draft);
  const nextPct = pct + PCT_STEP;
  const nextTier =
    typed !== undefined && typed > selectedIds.length && nextPct <= 100
      ? quote?.rows.find((r) => Math.round(r.pct * 100) === nextPct)
      : undefined;
  const missing = nextTier ? nextTier.contractCount - (typed ?? 0) : 0;

  const handleCount = (value: string) => {
    const digits = value.replace(/\D/g, "");
    const count = Math.min(Number(digits), total);
    setDraft(digits === "" ? "" : String(count));
    // Selection moves in whole steps: take the one at or below the count.
    onPct(
      total > 0 ? Math.floor(((count / total) * 100) / PCT_STEP) * PCT_STEP : 0,
      orderedIds
    );
  };

  const dimmed = hasSelection && (isFetching || !isCurrent);

  if (isError)
    return (
      <Alert.Root status="error" colorPalette="red">
        <Alert.Indicator />
        <Alert.Content>
          <Alert.Description>
            No pudimos cargar tus pre-aprobados.
          </Alert.Description>
        </Alert.Content>
        <Button size="sm" variant="outline" onClick={() => refetch()}>
          Reintentar
        </Button>
      </Alert.Root>
    );

  return (
    <Card.Root borderRadius="lg" border="none">
      <Card.Body p="5" display="flex" flexDirection="column" gap="4">
        <Box>
          <Heading size="xl">Elige los contratos que quieres afianzar</Heading>
          <Text color="gray.600">
            Entre más contratos elijas, más baja es la tasa. Ya traen datos de
            Simi.
          </Text>
        </Box>
        <Slider.Root
          min={0}
          max={100}
          step={PCT_STEP}
          value={[pct]}
          disabled={isLoading}
          onValueChange={(e) => {
            setDraft(null);
            onPct(e.value[0], orderedIds);
          }}
          colorPalette="palomma"
          maxW="xl"
        >
          <Slider.Label>
            Porcentaje de contratos a afianzar: <b>{pct}%</b>
          </Slider.Label>
          <Slider.Control>
            <Slider.Track>
              <Slider.Range />
            </Slider.Track>
            <Slider.Thumbs />
          </Slider.Control>
        </Slider.Root>
        <Box
          display="grid"
          gridTemplateColumns={{ base: "1fr", md: "repeat(3, 1fr)" }}
          gap="3"
        >
          <Kpi
            label="Seleccionados"
            note={
              nextTier
                ? `Suma ${missing} contrato${
                    missing === 1 ? "" : "s"
                  } más y tu tasa baja a ${formatRate(
                    rateFor(nextTier, withVat)
                  )}.`
                : undefined
            }
            value={
              <Box display="flex" alignItems="center" gap="2">
                <Input
                  w="24"
                  size="lg"
                  fontWeight="inherit"
                  inputMode="numeric"
                  aria-label="Número de contratos seleccionados"
                  disabled={isLoading}
                  value={draft ?? String(selectedIds.length)}
                  onChange={(e) => handleCount(e.target.value)}
                  onBlur={() => draft === "" && setDraft(null)}
                />
                <span>de {total}</span>
              </Box>
            }
          />
          <Kpi
            label={`Tasa con este volumen (${vatLabel(withVat)})`}
            value={row ? formatRate(rateFor(row, withVat)) : "—"}
            dimmed={dimmed}
          />
          <Kpi
            label="Ahorro estimado / año"
            value={row ? formatCop(row.monthlySaving * 12) : "—"}
            dimmed={dimmed}
          />
        </Box>
        {hasSelection && !!quote && !row && (
          <Text color="gray.600">
            Elige con la barra para ver la tasa de tu selección.
          </Text>
        )}
        {isQuoteError && !quote && (
          <Alert.Root status="error" colorPalette="red">
            <Alert.Indicator />
            <Alert.Content>
              <Alert.Description>
                No pudimos calcular la tasa para esta selección.
              </Alert.Description>
            </Alert.Content>
            <Button size="sm" variant="outline" onClick={() => refetchQuote()}>
              Reintentar
            </Button>
          </Alert.Root>
        )}
        <Box display="flex" flexWrap="wrap" gap="3">
          <DebouncedInput
            maxW="xs"
            inputMode="numeric"
            placeholder="Buscar por cédula"
            value={search}
            onDebouncedChange={onSearch}
          />
          <SelectFilter
            width="xs"
            options={providers}
            selectedOption={provider}
            onSelect={onProvider}
            placeholder="Aseguradora / Afianzadora"
          />
        </Box>
        <GenericTable isLoading={isLoading} columns={COLUMNS} size="sm">
          {visible?.map((screening) => (
            <SelectionRow
              key={screening.screeningId}
              screening={screening}
              selected={selected.has(screening.screeningId)}
            />
          ))}
        </GenericTable>
        <StepFooter onBack={onBack}>
          <Button disabled={!row || !isCurrent} onClick={onContinue}>
            {hasSelection
              ? `Continuar con ${selectedIds.length} contratos`
              : "Continuar"}
          </Button>
        </StepFooter>
      </Card.Body>
    </Card.Root>
  );
};

export default SelectionStep;
